import { MutableRefObject, useCallback, useEffect, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { ScaleLoader } from 'react-spinners';
import cn from 'classnames';

import { useCaption } from '@utils/providers/caption-context.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import { Session } from '@utils/types/editors/sessions.ts';
import {
  TranslationCaptionProvider,
  TranslationCaptionHandle
} from '@utils/providers/translation-caption-context.tsx';
import { CaptionEditorProvider } from '@utils/providers/caption-editor-context.tsx';
import { useCaptionHotkeys } from '@utils/providers/caption-hotkey-context.tsx';
import { CommentsProvider } from '@components/editors/shared/comments/comments-context.tsx';
import { AnchoredOverlay } from '@components/editors/shared/find-replace/anchored-overlay.tsx';
import { CaptionFindReplaceHost } from '@components/editors/caption-editor/caption-find-replace-host.tsx';
import { CommentsGutter } from '@components/editors/shared/comments/comments-gutter.tsx';
import { TranslationRowGroup } from './translation-row-group.tsx';
import {
  SplitGutterItem,
  SPLIT_GUTTER_WIDTH
} from '@components/editors/shared/comments/split-gutter-item.tsx';
import {
  CAPTION_ROW_HEIGHT,
  groupTranslationsBySource,
  rowSpans,
  type CaptionRowGroup
} from './pair-source-segment.ts';

/* eslint-disable no-restricted-syntax -- Semantic caption editor colors (subtitle palette, playback cues, pill foregrounds on tinted backgrounds) must stay stable across themes */

type Props = {
  session: Session;
  sessionId: string;
  scrollerRef: MutableRefObject<HTMLElement | null>;
  /**
   * Kept for API compatibility with caption-main.tsx; the pane no
   * longer sizes itself off the waveform (it uses `flex-1 min-h-0`
   * inside its grid cell instead).
   */
  hasWave?: boolean;
  selectedDocId: string | null;
  /** Ref to expose translation save/isDirty to parent */
  handleRef?: React.Ref<TranslationCaptionHandle>;
  /**
   * Reactive mirror of the translation's dirty flag. The ref-based
   * `isDirty` won't cause the parent to re-render, so the beforeunload
   * prompt would stay attached after a save. Pass a setState setter
   * from the parent to keep the prompt in sync.
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Source search text to sync to translation pane */
  sourceSearch?: string;
  /**
   * #2250 — the source document's segments. Rows pair by timing
   * overlap, not by array index, because the backend's
   * `split_multiple_lines` can turn one source segment into two
   * translated rows when the translation runs 3+ lines (see
   * `pair-source-segment.ts`). Index pairing was the root cause of
   * the "33 of 35 rows beside the wrong source line" bug on the AI
   * document in the #2250 reopen comment.
   */
  sourceSegments?: DocumentSubtitleSegmentV1[];
  /**
   * Reports how many slots each source row must fill so the source pane
   * can pad its rows to match (#2250). Index N is source segment N.
   */
  onRowSpansChange?: (spans: number[]) => void;
};

export default function EditableSplitCaptionPanel({
  session,
  sessionId,
  scrollerRef,
  selectedDocId,
  handleRef,
  onDirtyChange,
  sourceSearch,
  sourceSegments,
  onRowSpansChange
}: Props) {
  if (!selectedDocId) {
    return <EmptyState />;
  }

  return (
    <TranslationCaptionProvider
      key={selectedDocId}
      session={session}
      sessionId={sessionId}
      documentId={selectedDocId}
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
    >
      <CaptionEditorProvider>
        {/* #2250 — translation-pane comments provider. Scoped to the
            translation document id (comments here belong to the
            translation, not the source). Modal lives inside so the
            row-level compose trigger + click-through icon in
            `scriptix-caption-editor` reach the same context. The
            `getAnchoredText` lookup is wired inside <InnerPanel> so
            it can close over the translation's own segment array —
            splitting the mount from the lookup keeps this outer
            component free of a `useCaption` call. */}
        <InnerPanel
          scrollerRef={scrollerRef}
          sourceSearch={sourceSearch}
          sourceSegments={sourceSegments}
          onRowSpansChange={onRowSpansChange}
          sessionId={sessionId}
          selectedDocId={selectedDocId}
        />
      </CaptionEditorProvider>
    </TranslationCaptionProvider>
  );
}

const VIRTUOSO_COMPONENTS = { Item: SplitGutterItem };

function EmptyState() {
  const { t } = useTranslation();
  const { vars, cls } = useThemeVars();

  return (
    <div className="flex flex-col h-full" style={vars}>
      <div
        className={cn(
          'overflow-y-auto h-full mb-0.5 flex items-center justify-center p-8',
          cls.border
        )}
      >
        <div className={cn('text-center opacity-60', cls.text)}>
          <Globe size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">{t('no-translation-selected')}</p>
        </div>
      </div>
    </div>
  );
}

type InnerPanelProps = {
  scrollerRef: MutableRefObject<HTMLElement | null>;
  sourceSearch?: string;
  sourceSegments?: DocumentSubtitleSegmentV1[];
  onRowSpansChange?: (spans: number[]) => void;
  sessionId: string;
  selectedDocId: string;
};

function InnerPanel({
  scrollerRef,
  sourceSearch,
  sourceSegments,
  onRowSpansChange,
  sessionId,
  selectedDocId
}: InnerPanelProps) {
  const { t } = useTranslation();
  const { vars, cls } = useThemeVars();
  const { getColour } = useThemeContext();
  // Mount the Find & Replace popover inside this subtree so its
  // host runs with the TRANSLATION `useCaption()` scope — matches
  // + Replace All target the editable pane rather than the
  // read-only source. The outer caption-main popover is skipped in
  // split view (see caption-main.tsx CaptionFindReplacePortal).
  const { searchOpen, closeSearch } = useCaptionHotkeys();
  const {
    document: captionDocument,
    documentVersion,
    setSearch
  } = useCaption();

  // Sync source search text to translation context
  useEffect(() => {
    setSearch(sourceSearch ?? '');
  }, [sourceSearch, setSearch]);

  const segments: DocumentSubtitleSegmentV1[] = useMemo(() => {
    return captionDocument?.document ?? [];
  }, [captionDocument]);

  const handleScrollerRef = useCallback(
    (el: HTMLElement | Window | null) => {
      if (el && el instanceof HTMLElement) {
        scrollerRef.current = el;
      }
    },
    [scrollerRef]
  );

  // #2250 — one row per source segment. A source the backend split in
  // two holds both translated halves, so every later row stays level
  // with its source. The source pane pads its rows to the same spans.
  const groups = useMemo(
    () => groupTranslationsBySource(sourceSegments, segments),
    [sourceSegments, segments]
  );
  const spansKey = rowSpans(groups).join(',');
  useEffect(() => {
    onRowSpansChange?.(spansKey ? spansKey.split(',').map(Number) : []);
  }, [spansKey, onRowSpansChange]);

  const renderRow = useCallback(
    (_row: number, group: CaptionRowGroup) =>
      group ? <TranslationRowGroup group={group} segments={segments} /> : null,
    [segments]
  );

  return (
    <CommentsProvider
      sessionId={sessionId}
      documentId={selectedDocId}
      getAnchoredText={(from, to) => {
        // #2250 — one comment per translation segment; the modal
        // preview quotes the translated text at that segment.
        const seg = segments.find(
          s => String(s.start) === (from ?? '') && String(s.stop) === (to ?? '')
        );
        return seg?.text ?? '';
      }}
    >
      <div className="flex flex-col h-full min-h-0" style={vars}>
        {/* Rounded card matches the source pane's chrome. `flex-1 min-h-0`
          is what fills the split-view cell down to the transport bar —
          without it the card was collapsing to its content height and
          the pane ended halfway down while the source (a direct grid
          child) extended to the bottom. */}
        <div
          className={cn(
            'relative flex flex-col flex-1 min-h-0 m-2 rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]',
            cls.border
          )}
          style={{
            backgroundColor: getColour('secondLayer')
          }}
        >
          <header
            className="flex items-center justify-between gap-2 px-3 py-1 h-11 shrink-0 border-b"
            style={{ borderColor: getColour('border') }}
          >
            <span
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: getColour('inactiveText') }}
            >
              {t('translation')}
            </span>
          </header>

          <div role="list" className="flex-1 min-h-0 overflow-y-auto mb-0.5">
            {captionDocument === undefined ? (
              <div className="flex items-center justify-center h-full">
                <ScaleLoader color={'#5794f6'} />
              </div>
            ) : segments.length === 0 ? (
              <div className="flex items-center justify-center h-full p-8">
                <div className={cn('text-center opacity-60', cls.text)}>
                  <p className="text-sm">{t('no-segments-available')}</p>
                </div>
              </div>
            ) : (
              <Virtuoso
                key={`translation-virtuoso-${segments.length}-${documentVersion}`}
                data={groups}
                totalCount={groups.length}
                components={VIRTUOSO_COMPONENTS}
                style={{
                  height: '100%',
                  width: '100%',
                  overflowX: 'hidden'
                }}
                itemContent={renderRow}
                increaseViewportBy={200}
                defaultItemHeight={CAPTION_ROW_HEIGHT}
                overscan={20}
                scrollerRef={handleScrollerRef}
              />
            )}
          </div>
          <CommentsGutter
            scrollRootRef={scrollerRef}
            columnWidth={SPLIT_GUTTER_WIDTH}
          />
        </div>
      </div>
      {searchOpen && (
        <AnchoredOverlay anchorKey="search">
          <CaptionFindReplaceHost onClose={closeSearch} />
        </AnchoredOverlay>
      )}
    </CommentsProvider>
  );
}
